import React, { useEffect, useState } from "react";
import { Element as ScrollElement, scroller } from "react-scroll";
import classNames from "classnames";
import {
  BusinessPlanOptions,
  calculateBusinessPlanCost,
  calculateBusinessPlanTotals,
  getLocationPlans,
  getPlanAddonsForBusinessUser,
  toCurrency,
  User,
  Location,
  LocalProjectAddon,
} from "@ecologi/shared";
import { BusinessPlanSelect } from "./BusinessPlanSelect";
import { Button } from "../../Button";
import { PlanChangeConfirmModal } from "../../PlanChangeConfirmModal";
import { LocalProjectAddonBanner } from "../../LocalProjectAddonBanner";
import { CheckoutTotalTable } from "../../CheckoutTotalTable";
import { ContinueButton } from "../../Plan/components/ContinueButton";
import {
  CheckoutForm,
  CheckoutFormProps,
  CheckoutFormBaseProps,
} from "../../CheckoutForm";
import { YoureAwesome } from "../../YoureAwesome";
import styles from "../styles/BusinessCheckout.module.scss";

export const emptyBusinessPlan = {
  tier1: 0,
  tier2: 0,
  tier3: 0,
};

export const hasBusinessAddonsChanged = (
  newAddons: BusinessPlanOptions,
  oldAddons: BusinessPlanOptions
) => {
  const hasT1Changed = newAddons.tier1 !== oldAddons.tier1;
  const hasT2Changed = newAddons.tier2 !== oldAddons.tier2;
  const hasT3Changed = newAddons.tier3 !== oldAddons.tier3;

  return hasT1Changed || hasT2Changed || hasT3Changed;
};

function CheckoutFormWithWrapper(props: CheckoutFormProps) {
  return (
    <div className={styles.businessCheckoutWrapper}>
      <CheckoutForm {...props} />
    </div>
  );
}

type UpdateSubscriptionHandler = (args: {
  addons: BusinessPlanOptions;
  shortCode: string;
  localProjectAddon: LocalProjectAddon;
  numLocalTrees: number;
}) => Promise<unknown>;

type Props = {
  isLoading: boolean;
  isUpdateSubscription: boolean;
  updateSubscription: UpdateSubscriptionHandler;
  user: User | null;
  location: Location;
  settingsPageUrl: string;
  renderCheckoutForm: (props: CheckoutFormBaseProps) => JSX.Element;
};

export const BusinessCheckout = ({
  user,
  location = { countryCode: "US" },
  isLoading,
  isUpdateSubscription,
  updateSubscription,
  settingsPageUrl,
  renderCheckoutForm,
}: Props) => {
  const [isCheckoutVisible, setCheckoutVisibility] = useState(false);
  const [numLocalTrees, setNumLocalTrees] = useState(0);
  const [isUpdateModalVisible, setUpdateModalVisibility] = useState(false);
  const [displayLocalProject, setDisplayLocalProject] = useState(false);
  const [selectedBusinessPlanAddons, setSelectedBusinessPlanAddons] =
    useState<BusinessPlanOptions>(emptyBusinessPlan);

  const totalSelectedEmployees =
    selectedBusinessPlanAddons.tier1 +
    selectedBusinessPlanAddons.tier2 +
    selectedBusinessPlanAddons.tier3;
  const isValid = totalSelectedEmployees > 0;

  const {
    businessAddonsForLocation,
    businessPlanIdForLocation,
    currencySymbol,
    localProjectAddon,
  } = getLocationPlans(location);

  const totals = calculateBusinessPlanTotals(
    businessAddonsForLocation,
    selectedBusinessPlanAddons
  );
  const localAddonTotal =
    localProjectAddon && numLocalTrees
      ? numLocalTrees * localProjectAddon.subscriptionPrice
      : 0;

  useEffect(() => {
    // If there is a user trying to change their subscription,
    // make sure to set their current addons
    if (user?.subscription?.planId.includes("business")) {
      setSelectedBusinessPlanAddons(getPlanAddonsForBusinessUser(user));
      if (localProjectAddon) {
        const localAddon = user.subscription.addons.filter((a) => {
          const addonId = localProjectAddon.id;
          return a.id === addonId;
        });
        if (localAddon.length === 1) {
          setNumLocalTrees(localAddon[0].quantity);
          setDisplayLocalProject(true);
        }
      }
    }
  }, [user, businessPlanIdForLocation, localProjectAddon]);

  // If non-business plans end up here then tell them they're awesome
  if (user && !user.isBusinessPlan) {
    return <YoureAwesome user={user} />;
  }

  let currentTotal =
    user && user.subscription
      ? calculateBusinessPlanCost(
          businessAddonsForLocation,
          getPlanAddonsForBusinessUser(user)
        )
      : "";

  if (user && user.getLocalProjectAddons().length > 0 && localProjectAddon) {
    currentTotal = toCurrency(
      parseFloat(currentTotal) +
        localProjectAddon.subscriptionPrice *
          user.getLocalProjectAddons()[0].quantity
    );
  }

  const hasAddonsChanged =
    user && user.subscription
      ? hasBusinessAddonsChanged(
          selectedBusinessPlanAddons,
          getPlanAddonsForBusinessUser(user)
        )
      : false;

  const totalWithAddons = toCurrency(
    parseFloat(totals.price) + localAddonTotal
  );

  const userLocalProjectAddons = user ? user.getLocalProjectAddons() : [];
  const isLocalProjectAddonsChanged =
    userLocalProjectAddons.length > 0
      ? userLocalProjectAddons[0].quantity !== numLocalTrees
      : numLocalTrees > 0;

  const isUpdateButtonDisabled =
    isUpdateSubscription &&
    (!isValid ||
      !(hasAddonsChanged || isLocalProjectAddonsChanged) ||
      user?.isPaused);

  return (
    <>
      <BusinessPlanSelect
        isUpdateSubscription={isUpdateSubscription}
        location={location}
        selectedBusinessPlanAddons={selectedBusinessPlanAddons}
        setSelectedBusinessPlanAddons={setSelectedBusinessPlanAddons}
        numLocalTrees={numLocalTrees}
        onClickDisplayLocalProject={() => {
          setDisplayLocalProject(true);
          scroller.scrollTo("local-projects", {
            duration: 800,
            smooth: true,
          });
        }}
      />

      <ScrollElement name="local-projects">
        {displayLocalProject && (
          <>
            <LocalProjectAddonBanner
              {...{ numLocalTrees, setNumLocalTrees }}
              location={location}
            />
            {numLocalTrees > 0 && (
              <CheckoutTotalTable
                location={location}
                localProjectAddon={localProjectAddon}
                numLocalTrees={numLocalTrees}
                selectedPlanAddons={selectedBusinessPlanAddons}
                selectedPlanId={businessPlanIdForLocation}
              />
            )}
          </>
        )}
      </ScrollElement>

      <div className={styles.divider1} />

      {!isUpdateSubscription && (
        <div
          className={classNames(
            styles.buttonWrapper,
            displayLocalProject && styles.buttonWrapperLocalProject
          )}
          data-is-local-project-available={!!localProjectAddon}
          data-is-plan-selected={isValid}
        >
          <div
            className={styles.buttonDisabler}
            data-is-checkout-visible={isCheckoutVisible}
          >
            <ContinueButton
              isDisabled={!isValid}
              isBlock
              isBusinessPlan
              onClick={() => {
                setCheckoutVisibility(true);
                scroller.scrollTo("checkout", {
                  duration: 800,
                  smooth: true,
                });
              }}
            />
          </div>
        </div>
      )}

      <div className={styles.divider2} />

      {isUpdateSubscription && (
        <div>
          {user?.isPaused &&
            (hasAddonsChanged || isLocalProjectAddonsChanged) && (
              <p className={styles.pausedSubscriptionWarning}>
                Please resume your subscription before making any changes.
              </p>
            )}
          <div
            className={classNames(
              styles.buttonWrapper,
              displayLocalProject && styles.buttonWrapperLocalProject
            )}
          >
            <div
              className={styles.buttonDisabler}
              data-is-checkout-visible={isCheckoutVisible}
            >
              <Button
                disabled={isUpdateButtonDisabled}
                textOnlyWithBorder={isUpdateButtonDisabled}
                primary={!isUpdateButtonDisabled}
                onClick={async (e) => {
                  e.preventDefault();
                  setUpdateModalVisibility(true);
                }}
                text="Update your subscription"
                block
              />
            </div>
          </div>
          <a className={styles.subscriptionSettingsLink} href={settingsPageUrl}>
            Back to subscription settings
          </a>
        </div>
      )}

      <ScrollElement name="checkout">
        {isCheckoutVisible && (
          <section className={styles.businessCheckout}>
            {renderCheckoutForm({
              numLocalTrees,
              selectedPlanAddons: selectedBusinessPlanAddons,
              selectedPlanId: businessPlanIdForLocation,
            })}
          </section>
        )}
      </ScrollElement>

      <PlanChangeConfirmModal
        close={() => {
          setUpdateModalVisibility(false);
        }}
        confirm={async () => {
          await updateSubscription({
            addons: selectedBusinessPlanAddons,
            shortCode: businessPlanIdForLocation,
            localProjectAddon,
            numLocalTrees,
          });
          setUpdateModalVisibility(false);
        }}
        isLoading={isLoading}
        isVisible={isUpdateModalVisible}
        isBusiness
        newTotal={`${currencySymbol}${totalWithAddons}`}
      />
    </>
  );
};

BusinessCheckout.CheckoutForm = CheckoutFormWithWrapper;
